/* eslint-disable max-len */
/* eslint-disable space-before-function-paren */
/* eslint-disable camelcase */
/* eslint-disable keyword-spacing */
import ReactGA from 'react-ga4';
import * as actions from 'src/store/user/user.actions';
import * as agencyActions from 'src/store/user-info/user-info.actions';

import { userSelectedLocationInfoSelector, userRoleSelector } from 'src/store/user/user.selectors';
import { userAgenciesSelector } from 'src/store/user-info/user-info.selectors';

import { initializeAfterLogin, buildUserAnalyticsProperties } from 'src/store/initialization/initialization.operations';
import { syncFeatureFlagsOperation } from 'src/store/feature-flags/feature-flags.operations';
import { updateUserProperties } from 'src/utils/analytics';

import { getUserAgenciesRequest as fetchUserAgencies } from 'src/services/requests/sidebar/sidebar';
import { getAllAgencyTypes } from 'src/services/requests/cities-and-agencies/cities-and-agencies';
import { normalizeAllAgencyTypes } from 'src/services/requests/cities-and-agencies/normalize-all-agency-types';
import { isLytRole } from 'src/constants/roles.constants';

import { bookList } from 'src/constants/bookList';
import { NAV_REGISTRY } from 'src/config/navigation.registry';
import { getRestrictedRoutePrefixesForAgencyType } from 'src/config/navigation.utils';
import { featureFlagValuesSelector } from 'src/store/feature-flags/feature-flags.selectors';
import { route } from 'src/routing/navigation';

import * as requests from 'src/services/requests/user/user';

// Scot's unblocking fake so he can see the Map //////
const mockReturns = {
    pdx: {zipcode: 97035, number_of_intersections: 1, maestro_pk: 6, installed_date: null, timezone: 'America/Los_Angeles', ip_address: 'NULL', contact_person: 'Menard', contact_email: 'tim@lyt.ai', details: {center: {lat: 45.523969, lng: -122.663988}, agencies: [{name: 'tri', display: 'TriMet', type: 'transit', search: 'true'}]}, maestros: '{6}'},
    psv: {zipcode: 95054, number_of_intersections: 3, maestro_pk: 1, installed_date: null, timezone: 'America/Los_Angeles', ip_address: 'NULL', contact_person: 'NULL', contact_email: 'NULL', details: {center: {lat: 37.363571, lng: -121.890994}, agencies: [{name: 'psvfire', display: 'PSV Fire', type: 'transit', search: 'false'}]}, maestros: '{1,9,14,15}'}
};
////////// remove this block later ///////////////////


/**
 * Fetch agency list for the current user.
 *
 * For LYT-internal roles (lyt_admin, lyt_employee) we call
 *   GET /agencies/alltypes
 * which returns every agency across all types.
 *
 * For all other roles we call
 *   GET /agencies/cities/user
 * which returns only the agencies the user belongs to.
 */
export const getUserAgenciesOperation = () => (dispatch, getState) => {
    dispatch(agencyActions.getUserAgenciesRequest());

    const userRole = userRoleSelector(getState());
    const lytUser = isLytRole(userRole);

    if (lytUser) {
        return getAllAgencyTypes()
            .then((rows) => {
                const agencies = normalizeAllAgencyTypes(rows);
                dispatch(agencyActions.getUserAgenciesSuccess(agencies));
                dispatch(setUserSelectedLocationInitialOperation());
            })
            .catch((error) => {
                dispatch(agencyActions.getUserAgenciesError(error));
            });
    }

    return fetchUserAgencies()
        .then((response) => {
            dispatch(agencyActions.getUserAgenciesSuccess(response));
            dispatch(setUserSelectedLocationInitialOperation());
        })
        .catch((error) => {
            dispatch(agencyActions.getUserAgenciesError(error));
        });
};

export const getUserPersonalInformation = () => (dispatch) => {
    dispatch(actions.getUserPersonalInformationRequest());

    return requests
        .getUserPersonalInformationRequest()
        .then((response) => {
            dispatch(actions.getUserPersonalInformationSuccess(response));
            
            // Initialize application data after successful login
            // This centralizes all initial data fetching (roles, agencies, etc.)
            dispatch(initializeAfterLogin()).catch((initError) => {
                console.warn('Failed to initialize application data during login:', initError);
                // Don't fail the login process if initialization fails
            });
        })
        .catch((error) => {
            dispatch(actions.getUserPersonalInformationError(error));
        });
};

export const sendUserUpdateOperation = (data) => (dispatch) => {
    dispatch(actions.setUserPersonalInformationRequest());

    return requests
        .updateUserRequest(data)
        .then(response => {
            dispatch(actions.setUserPersonalInformationSuccess(response));
        })
        .catch((error) => {
            console.log('error', error);
            dispatch(actions.setUserPersonalInformationError(error));
        });
};

export const changePasswordOperation = (data) => async (dispatch) => {
    dispatch(actions.changePasswordRequest());
    try {
        const response = await requests
            .changePasswordRequest(data);
        dispatch(actions.changePasswordSuccess(response));
    } catch (error) {
        dispatch(actions.changePasswordError(error));
    }
};

export const setUserSelectedLocationInitialOperation = () => (dispatch, getStore) => {
    const { list } = userAgenciesSelector(getStore());
    const { agencyId } = userSelectedLocationInfoSelector(
        getStore()
    );
    // With redux-persist removed, agencyId is always '' on a fresh boot, so
    // we always fall through to picking list[0]. The membership-guard branch
    // is kept as a defense in case any future code path seeds agencyId before
    // this thunk runs — we never want to honor an agencyId the current user
    // is not actually a member of.
    const isMember = list.some((a) => a.agencies_id === agencyId);
    if (!agencyId || !isMember) {
        const firstElAgencyId = list[0]?.agencies_id || '';
        const firstElCityId = list[0]?.jurisdictions?.[0]?.id || '';

        dispatch(
            actions.setUserSelectedLocation({
                agencyId: firstElAgencyId,
                cityId: firstElCityId,
            })
        );
        dispatch(actions.setUserAgencyId(firstElAgencyId));
    } else {
        dispatch(actions.setUserAgencyId(agencyId));
    }
};

export const setUserAgencyIdOperation = (agencyId) => async (dispatch, getStore) => {
    ReactGA.gtag('event', 'lyt-agency-change', {
        agency: agencyId,
    });
    const { list } = userAgenciesSelector(getStore());
    const agency = list.find(({ agencies_id }) => agencies_id === agencyId);
    const { jurisdictions } = agency;

    // when we change the agency we need to add the first city from the list
    const firstElCityId = jurisdictions[0]?.id || '';
    const selectedLocationData = { agencyId, cityId: firstElCityId };

    dispatch(actions.setUserSelectedLocation(selectedLocationData));
    dispatch(actions.setUserAgencyId(agencyId));

    // Keep analytics user_properties in sync with the new agency so events
    // fired after this point are attributed to the correct agency segment.
    updateUserProperties(buildUserAnalyticsProperties(getStore()));

    await dispatch(syncFeatureFlagsOperation({ isRefresh: true }));

    // If the user switches to an agency whose type restricts the current route,
    // redirect them to Live Operations before re-fetching page data.
    const restrictedPrefixes = getRestrictedRoutePrefixesForAgencyType(
        NAV_REGISTRY,
        agency?.agency_type,
        featureFlagValuesSelector(getStore()),
    );
    if (restrictedPrefixes.length > 0) {
        const currentPath = window.location.pathname;
        const isOnRestrictedRoute = restrictedPrefixes.some(
            (prefix) => currentPath.startsWith(`/${prefix}`)
        );
        if (isOnRestrictedRoute) {
            route(bookList.live_operations.default);
        }
    }

    // Data re-fetching is handled by individual components via useEffect
    // reacting to agencyId changes through userAgencyIdSelector.
};
